/**
 * API client for prompt template management endpoints.
 */

import { fetchApi } from '../utils/api';
import { authHeader } from './authToken';
import {
  PromptTemplate,
  PromptTemplateDetail,
  PromptTemplateVersion,
  TestCase,
  TestRunSummary,
  TestRunDetail,
  TestRunResult,
  RoleMetricConfig,
  ServiceCriteria,
  TenantRole,
} from '../types/prompt';

function getAuthHeaders(): Record<string, string> {
  return authHeader();
}

// ============================================================
// Tenant
// ============================================================

export async function listTenants(): Promise<Array<{ tenant_id: string; tenant_name: string }>> {
  return fetchApi<Array<{ tenant_id: string; tenant_name: string }>>(
    '/api/tenant',
    { headers: getAuthHeaders() }
  );
}

export async function getTenantName(tenantId: string): Promise<string> {
  const tenants = await listTenants();
  const tenant = tenants.find((t) => t.tenant_id === tenantId);
  return tenant?.tenant_name || tenantId;
}

// ============================================================
// Templates
// ============================================================

export async function listTemplates(tenantId: string): Promise<PromptTemplate[]> {
  return fetchApi<PromptTemplate[]>(`/api/tenant/${tenantId}/prompt-templates`, {
    headers: getAuthHeaders(),
  });
}

export async function createTemplate(
  tenantId: string,
  data: { role: string; name: string; location_id?: string | null }
): Promise<{ id: string }> {
  return fetchApi<{ id: string }>(`/api/tenant/${tenantId}/prompt-templates`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: data,
  });
}

export async function getTemplate(templateId: string): Promise<PromptTemplateDetail> {
  return fetchApi<PromptTemplateDetail>(`/api/prompt-templates/${templateId}`, {
    headers: getAuthHeaders(),
  });
}

export async function updateTemplate(
  templateId: string,
  data: { name?: string }
): Promise<void> {
  await fetchApi(`/api/prompt-templates/${templateId}`, {
    method: 'PUT',
    headers: getAuthHeaders(),
    body: data,
  });
}

export async function deleteTemplate(templateId: string): Promise<void> {
  await fetchApi(`/api/prompt-templates/${templateId}`, {
    method: 'DELETE',
    headers: getAuthHeaders(),
  });
}

// ============================================================
// Versions
// ============================================================

export async function createVersion(
  templateId: string,
  data: {
    context: string;
    considerations: string;
    interaction_types: string;
    service_criteria: ServiceCriteria;
    menu: string;
    examples: string;
    output_format_template?: string | null;
    double_check_template?: string | null;
    // feat-156 — grade-aware offline-judge prompt (mig 101 column). Threaded
    // through the same version create/update path as double_check_template.
    judge_template?: string | null;
    recommendations_template?: string | null;
    analysis_requirements?: string | null;
    change_notes?: string | null;
  }
): Promise<{ id: string; version_number: number }> {
  return fetchApi<{ id: string; version_number: number }>(
    `/api/prompt-templates/${templateId}/versions`,
    {
      method: 'POST',
      headers: getAuthHeaders(),
      body: data,
    }
  );
}

export async function updateVersion(
  versionId: string,
  data: {
    context: string;
    considerations: string;
    interaction_types: string;
    service_criteria: ServiceCriteria;
    menu: string;
    examples: string;
    output_format_template?: string | null;
    double_check_template?: string | null;
    // feat-156 — grade-aware offline-judge prompt (mig 101 column). Threaded
    // through the same version create/update path as double_check_template.
    judge_template?: string | null;
    recommendations_template?: string | null;
    analysis_requirements?: string | null;
    change_notes?: string | null;
  }
): Promise<void> {
  await fetchApi(`/api/prompt-template-versions/${versionId}`, {
    method: 'PUT',
    headers: getAuthHeaders(),
    body: data,
  });
}

export async function getVersion(versionId: string): Promise<PromptTemplateVersion> {
  return fetchApi<PromptTemplateVersion>(`/api/prompt-template-versions/${encodeURIComponent(versionId)}`, {
    headers: getAuthHeaders(),
  });
}

export async function previewAssembledPrompt(
  versionId: string
): Promise<{ context: string; static_content: string }> {
  return fetchApi<{ context: string; static_content: string }>(
    `/api/prompt-template-versions/${versionId}/preview`,
    { headers: getAuthHeaders() }
  );
}

export async function activateVersion(
  versionId: string
): Promise<{ status: string; test_status: import('../types/prompt').TestStatus }> {
  return fetchApi<{ status: string; test_status: import('../types/prompt').TestStatus }>(
    `/api/prompt-template-versions/${versionId}/activate`,
    {
      method: 'POST',
      headers: getAuthHeaders(),
    },
  );
}

// ============================================================
// Test Cases
// ============================================================

export async function listTestCases(templateId: string): Promise<TestCase[]> {
  return fetchApi<TestCase[]>(`/api/prompt-templates/${templateId}/test-cases`, {
    headers: getAuthHeaders(),
  });
}

export async function createTestCase(
  templateId: string,
  data: {
    name: string;
    transcript_json: unknown[];
    expected_interactions?: Record<string, unknown> | null;
    source_url?: string | null;
  }
): Promise<{ id: string }> {
  return fetchApi<{ id: string }>(`/api/prompt-templates/${templateId}/test-cases`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: data,
  });
}

export async function updateTestCase(
  testCaseId: string,
  data: {
    name?: string;
    transcript_json?: unknown[];
    expected_interactions?: Record<string, unknown> | null;
  }
): Promise<void> {
  await fetchApi(`/api/prompt-test-cases/${testCaseId}`, {
    method: 'PUT',
    headers: getAuthHeaders(),
    body: data,
  });
}

export async function deleteTestCase(testCaseId: string): Promise<void> {
  await fetchApi(`/api/prompt-test-cases/${testCaseId}`, {
    method: 'DELETE',
    headers: getAuthHeaders(),
  });
}

export async function setGroundTruth(
  testCaseId: string,
  resultJson: Record<string, unknown>
): Promise<void> {
  await updateTestCase(testCaseId, { expected_interactions: resultJson });
}

export async function clearGroundTruth(testCaseId: string): Promise<void> {
  await updateTestCase(testCaseId, { expected_interactions: null });
}

// ============================================================
// Test Runs
// ============================================================

export async function runTest(
  testCaseId: string,
  versionId: string
): Promise<TestRunResult> {
  return fetchApi<TestRunResult>(
    `/api/prompt-test-cases/${testCaseId}/run/${versionId}`,
    {
      method: 'POST',
      headers: getAuthHeaders(),
    }
  );
}

export async function listTestRuns(templateId: string): Promise<TestRunSummary[]> {
  return fetchApi<TestRunSummary[]>(`/api/prompt-templates/${templateId}/test-runs`, {
    headers: getAuthHeaders(),
  });
}

export async function getTestRun(runId: string): Promise<TestRunDetail> {
  return fetchApi<TestRunDetail>(`/api/prompt-test-runs/${runId}`, {
    headers: getAuthHeaders(),
  });
}

// ============================================================
// Roles & Metric Config
// ============================================================

export async function listRoles(tenantId: string): Promise<TenantRole[]> {
  return fetchApi<TenantRole[]>(`/api/tenant/${tenantId}/roles`, {
    headers: getAuthHeaders(),
  });
}

export async function getMetricConfig(
  tenantId: string,
  role: string,
  locationId?: string | null
): Promise<RoleMetricConfig> {
  let url = `/api/tenant/${tenantId}/metric-config?role=${encodeURIComponent(role)}`;
  if (locationId) {
    url += `&location_id=${encodeURIComponent(locationId)}`;
  }
  return fetchApi<RoleMetricConfig>(url, {
    headers: getAuthHeaders(),
  });
}

export async function getMetricConfigByVersion(
  versionId: string
): Promise<RoleMetricConfig> {
  return fetchApi<RoleMetricConfig>(`/api/metric-config-by-version/${encodeURIComponent(versionId)}`, {
    headers: getAuthHeaders(),
  });
}
